<script lang="ts">
  import { ClipboardService } from '$lib/services/ClipboardService'
  import { EditorStateService } from '$lib/services/EditorStateService'
  import { undoRedoService } from '$lib/services/UndoRedoService'
  import type { AppliedCorrectionInfo } from '$lib/services/UndoRedoService'
  
  /**
   * ClipboardController: Handles all clipboard operations
   * Single Responsibility: Copy, cut, and paste text operations
   */
  
  interface Props {
    editorText: string
    hasChecked: boolean
    textManager: any
    textareaElement: HTMLTextAreaElement | null  // Add reference to actual textarea
    correctionStates: Map<string, AppliedCorrectionInfo>
    lastHistoryText: string | null
    showSplitEditor: boolean
    tipTapEditor: any | null  // Add reference to TipTap editor for markdown copy
    onStatusMessage: (message: string) => void
    onTextUpdate: (text: string, isManualHistory?: boolean) => void
    onClearErrors: () => void
  }
  
  let {
    editorText = $bindable(''),
    hasChecked,
    textManager,
    textareaElement,  // Add textarea reference
    correctionStates,
    lastHistoryText,
    showSplitEditor,
    tipTapEditor,  // Add TipTap editor reference
    onStatusMessage,
    onTextUpdate,
    onClearErrors
  }: Props = $props()
  
  let historyTimer: ReturnType<typeof setTimeout> | null = null
  
  export async function handleCopyText() {
    // Don't handle copy in SplitView when in edit mode - let native selection work
    if (showSplitEditor) {
      const activeElement = document.activeElement
      if (activeElement && (activeElement.getAttribute('contenteditable') === 'true' ||
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA')) {
        // Let the browser handle the copy naturally
        return
      }
    }

    // Check if GenerationBar input is focused
    const activeElement = document.activeElement
    if (activeElement && activeElement.classList.contains('prompt-input')) {
      // Let the browser handle copy naturally for GenerationBar
      document.execCommand('copy')
      return
    }

    // Check if TipTap editor is active (reading mode) and use markdown
    if (tipTapEditor) {
      try {
        // Check if there's a selection in TipTap
        const selectedMarkdown = tipTapEditor.getSelectionAsMarkdown?.()

        if (selectedMarkdown) {
          // Copy selected markdown
          const success = await ClipboardService.writeText(selectedMarkdown)
          if (success) {
            onStatusMessage('선택한 텍스트를 복사했습니다')
          } else {
            onStatusMessage('복사에 실패했습니다')
          }
          return
        }

        // No selection - copy entire content as markdown
        const fullMarkdown = tipTapEditor.getTextContent?.()
        if (fullMarkdown) {
          const success = await ClipboardService.writeText(fullMarkdown)
          if (success) {
            onStatusMessage('클립보드에 복사되었습니다')
          } else {
            onStatusMessage('복사에 실패했습니다')
          }
          return
        }
      } catch (error) {
        console.error('[ClipboardController] Failed to copy from TipTap:', error)
        // Fall through to regular copy
      }
    }

    // Use the specific textarea element passed as prop (source mode)
    if (textareaElement && textareaElement.selectionStart !== textareaElement.selectionEnd) {
      // There is a selection - copy only the selected text
      const start = textareaElement.selectionStart
      const end = textareaElement.selectionEnd
      const selectedText = editorText.substring(start, end)

      // Copy selected text to clipboard
      const success = await ClipboardService.writeText(selectedText)
      if (success) {
        onStatusMessage('선택한 텍스트를 복사했습니다')
      } else {
        onStatusMessage('복사에 실패했습니다')
      }
    } else {
      // No selection - copy entire text
      const success = await ClipboardService.writeText(editorText)
      if (success) {
        onStatusMessage('클립보드에 복사되었습니다')
      } else {
        onStatusMessage('복사에 실패했습니다')
      }
    }
  }
  
  export async function handleCutText() {
    // Don't handle cut in SplitView when in edit mode - let native selection work
    if (showSplitEditor) {
      const activeElement = document.activeElement
      if (activeElement && (activeElement.getAttribute('contenteditable') === 'true' || 
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA')) {
        // Let the browser handle the cut naturally
        return
      }
    }
    
    // Check if GenerationBar input is focused
    const activeElement = document.activeElement
    if (activeElement && activeElement.classList.contains('prompt-input')) {
      // Let the browser handle cut naturally for GenerationBar
      document.execCommand('cut')
      return
    }
    
    // Use the specific textarea element passed as prop
    if (textareaElement && textareaElement.selectionStart !== textareaElement.selectionEnd) {
      // There is a selection - cut only the selected text
      const start = textareaElement.selectionStart
      const end = textareaElement.selectionEnd
      const selectedText = editorText.substring(start, end)
      
      // Save previous text for history
      const previousTextForHistory = editorText
      
      // Copy selected text to clipboard
      const success = await ClipboardService.writeText(selectedText)
      if (success) {
        // Remove selected text from editor
        const newText = editorText.substring(0, start) + editorText.substring(end)
        
        // Clear errors since text changed
        onClearErrors()
        
        // Add to history
        undoRedoService.addEntry('edit', newText, correctionStates, {
          description: '선택 텍스트 잘라내기',
          previousText: previousTextForHistory
        }, [], false, new Set())
        
        onTextUpdate(newText, true)
        
        // Restore cursor position
        setTimeout(() => {
          textareaElement?.setSelectionRange(start, start)
          textareaElement?.focus()
        }, 0)
        
        onStatusMessage('선택한 텍스트를 잘라냈습니다')
      } else {
        onStatusMessage('잘라내기에 실패했습니다')
      }
    } else {
      // No selection - cut entire text
      const previousTextForHistory = editorText
      
      // Use ClipboardService for clipboard operations
      const success = await ClipboardService.writeText(editorText)
      if (success) {
        // Clear the editor after copying to clipboard
        // Clear errors
        onClearErrors()
        
        // Add to history
        undoRedoService.addEntry('edit', '', correctionStates, {
          description: '텍스트 잘라내기',
          previousText: previousTextForHistory
        }, [], false, new Set())
        
        onTextUpdate('', true)
        textManager?.clearText()
        
        onStatusMessage('클립보드에 잘라냈습니다')
      } else {
        onStatusMessage('잘라내기에 실패했습니다')
      }
    }
  }
  
  export async function handlePasteText() {
    // Don't handle paste in SplitView when in edit mode - let native selection work
    if (showSplitEditor) {
      const activeElement = document.activeElement
      if (activeElement && (activeElement.getAttribute('contenteditable') === 'true' || 
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA')) {
        // Let the browser handle the paste naturally
        return
      }
    }
    
    // Check if GenerationBar input is focused
    const activeElement = document.activeElement
    if (activeElement && activeElement.classList.contains('prompt-input')) {
      // Let the browser handle paste naturally for GenerationBar
      const clipboardText = await ClipboardService.readText()
      if (clipboardText) {
        // Insert at cursor position
        const input = activeElement as HTMLTextAreaElement
        const start = input.selectionStart
        const end = input.selectionEnd
        const currentValue = input.value
        input.value = currentValue.slice(0, start) + clipboardText + currentValue.slice(end)
        input.selectionStart = input.selectionEnd = start + clipboardText.length
        // Trigger input event for Svelte binding
        input.dispatchEvent(new Event('input', { bubbles: true }))
      }
      return
    }
    
    // Save previous text for history
    const previousTextForHistory = editorText
    
    // Use ClipboardService for clipboard operations
    const clipboardText = await ClipboardService.readText()
    if (clipboardText) {
      let newText: string
      let cursorPosition: number
      
      if (textareaElement) {
        const start = textareaElement.selectionStart
        const end = textareaElement.selectionEnd
        
        // Insert clipboard text at cursor position (or replace selection)
        newText = editorText.substring(0, start) + clipboardText + editorText.substring(end)
        cursorPosition = start + clipboardText.length
      } else {
        // No textarea found - append to end
        newText = editorText + clipboardText
        cursorPosition = newText.length
      }
      
      // Clear any pending history timer
      if (historyTimer) {
        clearTimeout(historyTimer)
        historyTimer = null
      }
      
      // If pasting from empty state, ensure empty state is in history
      if (previousTextForHistory === '' && lastHistoryText !== '') {
        undoRedoService.addEntry('edit', '', correctionStates, {
          description: '빈 상태',
          previousText: ''
        }, [], false, new Set())
      }
      
      // Clear errors since text changed
      onClearErrors()
      
      // Add to history
      undoRedoService.addEntry('edit', newText, correctionStates, {
        description: '텍스트 붙여넣기',
        previousText: previousTextForHistory
      }, [], false, new Set())
      
      onTextUpdate(newText, true)
      textManager?.setText(newText)
      
      // Restore cursor position after paste
      if (textareaElement) {
        setTimeout(() => {
          textareaElement.setSelectionRange(cursorPosition, cursorPosition)
          textareaElement.focus()
        }, 0)
      }
      
      onStatusMessage('텍스트를 붙여넣었습니다')
    } else {
      onStatusMessage('붙여넣기에 실패했습니다')
    }
  }
  
  export function handleClearText() {
    // Use the specific textarea element passed as prop
    if (textareaElement && textareaElement.selectionStart !== textareaElement.selectionEnd) {
      // There is a selection - clear only the selected text
      const start = textareaElement.selectionStart
      const end = textareaElement.selectionEnd
      
      // Save previous text for history
      const previousTextForHistory = editorText
      
      // Remove selected text
      const newText = editorText.substring(0, start) + editorText.substring(end)
      
      // Clear errors since text changed
      onClearErrors()
      
      // Add to history
      undoRedoService.addEntry('edit', newText, correctionStates, {
        description: '선택 텍스트 삭제',
        previousText: previousTextForHistory
      }, [], false, new Set())
      
      onTextUpdate(newText, true)
      textManager?.setText(newText)
      
      // Restore cursor position
      setTimeout(() => {
        textareaElement?.setSelectionRange(start, start)
        textareaElement?.focus()
      }, 0)
      
      onStatusMessage('선택한 텍스트를 삭제했습니다')
    } else {
      // No selection - clear entire text
      const previousTextForHistory = editorText
      
      // Add to history before clearing
      if (editorText) {
        undoRedoService.addEntry('edit', '', correctionStates, {
          description: '텍스트 전체 삭제',
          previousText: previousTextForHistory
        }, [], false, new Set())
      }
      
      // Clear errors
      onClearErrors()
      
      onTextUpdate('', true)
      textManager?.clearText()
      
      onStatusMessage('텍스트를 모두 지웠습니다')
    }
  }
</script>